import { useEffect, useRef } from 'react';
import { useProfile } from '@/hooks/useProfile';
import { analyticsHistoryDays, isPremiumActive } from '@/lib/premiumFeatures';
import { syncExistingEntitlements } from '@/lib/premiumPurchase';
import { isPurchasesAvailable } from '@/lib/straivPurchases';
import { Analytics } from '@/lib/analytics';

/**
 * Client-side Premium state (backed by `profiles.subscription_*`).
 * On iOS, syncs StoreKit entitlements once per session.
 */
export function usePremium() {
  const { profile, refetch } = useProfile();
  const syncedRef = useRef(false);
  const isPremium = isPremiumActive(profile ?? null);

  useEffect(() => {
    if (syncedRef.current || !isPurchasesAvailable()) return;
    syncedRef.current = true;
    void syncExistingEntitlements()
      .then((result) => {
        if ((result as { premium?: boolean } | null | undefined)?.premium) {
          void refetch();
        }
      })
      .catch((err) => console.warn('[usePremium] entitlement sync', err));
  }, [refetch]);

  useEffect(() => {
    void Analytics.setPremiumStatus(isPremium);
  }, [isPremium]);

  return {
    isPremium,
    subscriptionTier: profile?.subscription_tier ?? 'free',
    subscriptionExpiresAt: profile?.subscription_expires_at ?? null,
    analyticsHistoryDays: analyticsHistoryDays(isPremium),
    refetchProfile: refetch,
  };
}
